<template>
  <div v-loading="isLoading" class="flex flex-col rounded-5xl text-l-medium text-text-active gap-2">
    <div class="ml-1.5 mb-4">
      <ReturnButton />
    </div>
    <div
      v-if="!isEmptyState"
      class="flex flex-col flex-1 gap-2 min-h-0 glass-frosted p-4 rounded-6xl w-fit max-w-full"
    >
      <!-- 顶部标签切换 -->
      <Tabs v-model="activeTab" :items="tabItems" />
      <!-- 主内容区：配置卡片列表 -->
      <Main :active-tab="activeTab" class="mt-2 flex-1" />
    </div>

    <!-- 空状态：无 tab 时显示 -->
    <Empty v-else :message-key="emptyMessageKey" />
    <!-- 游戏关联 -->
    <GameApp v-if="showGameApp" />
  </div>
</template>

<script lang="ts" setup>
  /**
   * @file index.vue
   * @description 配置管理主页面，集成标签页、配置卡片和游戏关联组件
   * @created 2026-02-03
   */

  import { ref, computed, watch } from 'vue'
  import { isElectron } from '@/utils/env'
  import { isWindows } from '@/utils/ua'
  import { storeToRefs } from 'pinia'
  import { BaseDevice } from '@rapoo/device-sdk'
  import { useDeviceStore, useConfigurationStore } from '@/stores'
  import { hasConfigCapability } from '@/composables/useConfigName'
  import Tabs from './Tabs.vue'
  import Main from './Main.vue'
  import GameApp from './GameApp.vue'
  import Empty from './Empty.vue'
  import ReturnButton from '@/components/ReturnButton.vue'

  // 定义 TabItem 接口，避免从 .vue 文件导入类型可能导致的问题
  interface TabItem {
    label: string
    value: string
  }

  // 状态管理
  const deviceStore = useDeviceStore()
  const { currentDevice, expandedDevices } = storeToRefs(deviceStore)
  const configStore = useConfigurationStore()
  const { isLoading } = storeToRefs(configStore)

  // 游戏关联依赖桌面端能力，且当前只在 Windows Electron 客户端开放。
  const showGameApp = computed(() => isElectron() && isWindows)

  /**
   * 生成设备稳定标识 key：vendorId_currentPid
   * 设备重连时 id 会变，但 pid/vid 不变，用此 key 确保 tab 能稳定匹配。
   */
  function getDeviceKey(device: BaseDevice | null): string {
    if (!device) return ''
    return `${device.manifest.hardware.vendorId}_${device.manifest.hardware.currentPid}`
  }

  /** 从 expandedDevices 中根据设备 key 查找对应的 original 设备实例 */
  function findDeviceByKey(key: string): BaseDevice | undefined {
    return expandedDevices.value.find((d) => getDeviceKey(d.original) === key)?.original
  }

  // 生成 Tabs 数据
  // 结构: [ ...devices ]
  // 使用 vendorId_currentPid 作为 tab value，避免设备重连后 id 变化导致失配。
  const tabItems = computed<TabItem[]>(() => {
    const items: TabItem[] = []

    expandedDevices.value.forEach(({ original }) => {
      // 确保 device 和必要属性存在
      if (!original || !original.manifest) return

      // 过滤掉没有用户配置能力的设备（底座恢复出厂也会挂 features.config）
      if (!hasConfigCapability(original)) return

      // 有子设备的话直接排除（底座本身）
      if (original.subDevices && original.subDevices.length > 0) return

      const key = getDeviceKey(original)

      // 检查是否已经包含该设备（按 key 去重）
      if (!items.some((item) => item.value === key)) {
        // console.log(`Adding device to tabs: ${original.manifest.model} (key: ${key})`)
        items.push({
          label: original.manifest.model || 'Unknown Device',
          value: key,
        })
      }
    })

    return items
  })

  const isEmptyState = computed(() => tabItems.value.length === 0)

  /** 空状态消息 key：没有任何可配置设备时，提示"没有配置功能" */
  const emptyMessageKey = computed(() => {
    if (isEmptyState.value && currentDevice.value) {
      return 'home.emptyState.notFoundConfig2'
    }
    return undefined
  })

  function isConfigTabKey(key: string): boolean {
    return tabItems.value.some((item) => item.value === key)
  }

  /** 当前设备不可配置时，回退到仍要展示的配置 tab（底座优先用其子设备） */
  function resolveConfigTabKey(device: BaseDevice | null): string {
    const items = tabItems.value
    if (items.length === 0) return ''

    const key = getDeviceKey(device)
    if (key && isConfigTabKey(key)) return key

    const childKey = device?.subDevices
      ?.map((child) => getDeviceKey(child))
      .find((item) => isConfigTabKey(item))
    if (childKey) return childKey

    return items[0].value
  }

  // 当前选中的 Tab 值 (vendorId_currentPid)
  const activeTab = ref('')

  // 监听 Tab 切换，同步更新 currentDevice
  watch(activeTab, async (newVal) => {
    if (newVal) {
      const device = findDeviceByKey(newVal)
      if (device && device !== currentDevice.value) {
        await deviceStore.selectDevice(device)
      }
    }
  })

  // 监听 currentDevice 变化，同步更新 activeTab
  watch(
    currentDevice,
    (newDev) => {
      const nextKey = resolveConfigTabKey(newDev)
      if (nextKey) {
        if (activeTab.value !== nextKey) activeTab.value = nextKey
        return
      }
      activeTab.value = ''
    },
    { immediate: true },
  )

  // 可配置设备列表变化时，保证 activeTab 落在有效 tab 上
  watch(tabItems, (items) => {
    if (!items.length) {
      activeTab.value = ''
      return
    }
    if (!isConfigTabKey(activeTab.value)) {
      activeTab.value = resolveConfigTabKey(currentDevice.value)
    }
  })
</script>

<style lang="scss" scoped></style>
